<template>
  <section v-show="active" class="talhao-map-panel">
    <div class="talhao-map-header">
      <div>
        <h3><b-icon-map-fill /> Localização do Talhão</h3>
        <p>Clique no mapa para marcar a coordenada do talhão.</p>
      </div>
    </div>

    <div class="talhao-map-content">
      <div ref="map" class="talhao-map-canvas"></div>
      <button
        v-if="map"
        type="button"
        class="map-layer-toggle"
        :aria-pressed="isSatellite"
        :title="isSatellite ? 'Exibir mapa padrão' : 'Exibir mapa por satélite'"
        @click="toggleMapLayer"
      >
        {{ isSatellite ? "Mapa" : "Satélite" }}
      </button>
      <div v-if="isLoading" class="talhao-map-feedback">
        <b-spinner small variant="success" />
        <span>{{ loadingMessage }}</span>
      </div>
      <div v-if="errorMessage" class="talhao-map-error">
        <b-icon-exclamation-triangle-fill /> {{ errorMessage }}
      </div>
    </div>
  </section>
</template>

<script>
const MAPS_SCRIPT_ID = "winfit-google-maps-sdk-talhao";
const DEFAULT_CENTER = { lat: -14.235, lng: -51.9253 };

function loadGoogleMaps() {
  if (window.google?.maps) return Promise.resolve(window.google.maps);

  const existing = document.getElementById(MAPS_SCRIPT_ID);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(window.google.maps), { once: true });
      existing.addEventListener("error", reject, { once: true });
    });
  }

  const existingMain = document.getElementById("winfit-google-maps-sdk");
  if (existingMain) {
    return new Promise((resolve) => {
      const check = () => {
        if (window.google?.maps) resolve(window.google.maps);
        else setTimeout(check, 100);
      };
      check();
    });
  }

  return Promise.reject(new Error("Google Maps SDK não disponível."));
}

export default {
  name: "TalhaoMap",
  props: {
    active: { type: Boolean, default: false },
    latitude: { type: [String, Number], default: null },
    longitude: { type: [String, Number], default: null },
    propertyLatitude: { type: [String, Number], default: null },
    propertyLongitude: { type: [String, Number], default: null },
  },
  data() {
    return {
      map: null,
      marker: null,
      mapClickListener: null,
      isLoading: false,
      loadingMessage: "Carregando mapa...",
      errorMessage: "",
      isSatellite: false,
    };
  },
  computed: {
    selectedCoordinates() {
      if (
        this.latitude === null ||
        this.latitude === "" ||
        this.longitude === null ||
        this.longitude === ""
      ) {
        return null;
      }
      const lat = Number(this.latitude);
      const lng = Number(this.longitude);
      return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
    },
    propertyCenter() {
      if (
        this.propertyLatitude === null ||
        this.propertyLatitude === "" ||
        this.propertyLongitude === null ||
        this.propertyLongitude === ""
      ) {
        return null;
      }
      const lat = Number(this.propertyLatitude);
      const lng = Number(this.propertyLongitude);
      return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(isActive) {
        if (isActive) this.activateMap();
      },
    },
    selectedCoordinates(coordinates) {
      if (coordinates && this.map) this.setMarker(coordinates, true);
    },
  },
  methods: {
    async activateMap() {
      await this.$nextTick();

      if (this.map) {
        window.google.maps.event.trigger(this.map, "resize");
        const center = this.marker?.getPosition() || this.map.getCenter();
        if (center) this.map.setCenter(center);
        return;
      }

      await this.initializeMap();
    },
    async initializeMap() {
      if (!this.$refs.map || this.isLoading) return;

      this.isLoading = true;
      this.loadingMessage = "Carregando mapa...";
      this.errorMessage = "";

      try {
        await loadGoogleMaps();

        if (!this.$refs.map || this._isBeingDestroyed) return;

        const center = this.selectedCoordinates || this.propertyCenter || DEFAULT_CENTER;
        const zoom = this.selectedCoordinates || this.propertyCenter ? 16 : 4;

        this.map = new window.google.maps.Map(this.$refs.map, {
          center,
          zoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        });

        this.mapClickListener = this.map.addListener("click", ({ latLng }) => {
          const coordinates = { lat: latLng.lat(), lng: latLng.lng() };
          this.setMarker(coordinates, true);
          this.$emit("location-selected", {
            latitude: coordinates.lat.toFixed(7),
            longitude: coordinates.lng.toFixed(7),
          });
        });

        if (this.selectedCoordinates) {
          this.setMarker(this.selectedCoordinates, true);
        } else if (this.propertyCenter) {
          this.setMarker(this.propertyCenter, false);
        }
      } catch (error) {
        this.errorMessage =
          error.message || "Não foi possível inicializar o mapa.";
      } finally {
        this.isLoading = false;
      }
    },
    setMarker(coordinates, centerMap = false) {
      if (!this.map) return;

      if (!this.marker) {
        this.marker = new window.google.maps.Marker({
          map: this.map,
          position: coordinates,
        });
      } else {
        this.marker.setPosition(coordinates);
      }

      if (centerMap) this.map.panTo(coordinates);
    },
    toggleMapLayer() {
      if (!this.map) return;

      this.isSatellite = !this.isSatellite;
      this.map.setMapTypeId(
        this.isSatellite
          ? window.google.maps.MapTypeId.HYBRID
          : window.google.maps.MapTypeId.ROADMAP
      );
    },
  },
  beforeDestroy() {
    if (this.mapClickListener && window.google?.maps) {
      window.google.maps.event.removeListener(this.mapClickListener);
    }
    if (this.marker) this.marker.setMap(null);
    this.mapClickListener = null;
    this.marker = null;
    this.map = null;
  },
};
</script>

<style scoped>
.talhao-map-panel {
  height: 100%;
  min-height: 620px;
  padding: 6px;
  border: 1px solid #e2e8e6;
  border-radius: 5px;
  background: #fff;
}

.talhao-map-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 5px;
  margin: 0 5px;
}

.talhao-map-header h3 {
  margin: 0;
  color: #006256;
  font-size: 15px;
  font-weight: 700;
}
.talhao-map-header p {
  margin: 4px 0 0;
  color: #6c757d;
  font-size: 11px;
}
.talhao-map-content {
  position: relative;
  height: calc(100% - 58px);
  min-height: 540px;
}
.talhao-map-canvas {
  width: 100%;
  height: 100%;
  min-height: 540px;
  border-radius: 8px;
  background: #eef2f1;
}
.map-layer-toggle {
  position: absolute;
  z-index: 1;
  top: 10px;
  left: 10px;
  padding: 7px 12px;
  border: 1px solid #d7e2df;
  border-radius: 4px;
  background: #fff;
  color: #006256;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  cursor: pointer;
}
.map-layer-toggle:hover,
.map-layer-toggle:focus {
  border-color: #006256;
  outline: none;
}
.talhao-map-feedback,
.talhao-map-error {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.talhao-map-feedback {
  background: rgba(255, 255, 255, 0.95);
  color: #006256;
}
.talhao-map-error {
  background: #fff3f3;
  color: #a01818;
}

@media (max-width: 991.98px) {
  .talhao-map-panel {
    min-height: 480px;
  }
  .talhao-map-content,
  .talhao-map-canvas {
    min-height: 400px;
  }
}
</style>
